import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  getAuthToken,
  createAuthAxios,
  handleApiError,
} from "../utils/apiUtils";
import { AUTH_ENDPOINTS, API_BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";
import authService from "../services/authService";
import profilePhotoService from "../services/profilePhotoService";
import profileService from "../services/profileService";
import { clearPhotoCache, updatePhotoCache } from "../utils/profilePhotoCache";
import socketManager from "../services/socketManager";

// Create the context
export const UserContext = createContext();

// Custom hook for using the context
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch user profile function - Now properly fetches from API
  const fetchUserProfile = useCallback(async () => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated || !getAuthToken()) {
return user;
      }
// Fetch fresh user data from the API
      const freshUserData = await profileService.getUserProfile();

      if (freshUserData) {
        // Merge with existing user data to preserve token and other auth info
        const existingUser = JSON.parse(localStorage.getItem("user")) || {};
        const updatedUser = {
          ...existingUser,
          ...freshUserData,
          // Preserve authentication tokens
          token: existingUser.token,
          refreshToken: existingUser.refreshToken,
        };

        // Update localStorage and state
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
return updatedUser;
      }

      return user;
    } catch (error) {
// Return existing user data on error
      return user;
    }
  }, [user, isAuthenticated]);

  // Load user data from local storage on mount and handle Google OAuth callback
  useEffect(() => {
    const loadUser = () => {
      try {
        // First, check for Google OAuth callback
        const googleUser = authService.handleGoogleCallback();
        if (googleUser) {
          setUser(googleUser);
          setIsAuthenticated(true);

          // 🔌 Initialize sockets for Google OAuth user - immediate for faster connection
socketManager.initializeAll();
          return;
        }

        // If no Google callback, load from localStorage
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData && getAuthToken()) {
          setUser(userData);
          setIsAuthenticated(true);

          // 🔌 Initialize sockets for existing session - immediate for faster connection
socketManager.initializeAll();
        }
      } catch (error) {
// Clear potentially corrupted data
        localStorage.removeItem("user");
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Login function using authService
  const login = async (credentials) => {
    try {
      const userData = await authService.login(credentials);
      setUser(userData);
      setIsAuthenticated(true);

      // 🔌 Initialize all socket connections after login - immediate for faster connection
socketManager.initializeAll();

      // Immediately fetch fresh profile data to ensure we have the latest onboarding status
      try {
// Small delay to ensure backend has processed the login
        await new Promise((resolve) => setTimeout(resolve, 100));
        const freshUserData = await profileService.getUserProfile();

        if (freshUserData) {
          // Merge fresh data with login response, preserving tokens
          const updatedUser = {
            ...userData,
            ...freshUserData,
            // Preserve authentication tokens from login response
            token: userData.token,
            refreshToken: userData.refreshToken,
          };

          // Update both localStorage and state with fresh data
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setUser(updatedUser);
return updatedUser;
        }
      } catch (profileError) {
// Continue with login data if profile fetch fails
      }

      return userData;
    } catch (error) {
      throw error;
    }
  };

  // Signup function using authService
  const signup = async (userData) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Google login function using authService
  const googleLogin = () => {
    authService.initiateGoogleLogin();
  };

  // Save onboarding data using authService
  const saveOnboardingData = async (onboardingData) => {
    try {
      const updatedUser = await authService.saveOnboarding(onboardingData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  // Handle Google OAuth callback using authService
  const handleGoogleCallback = () => {
    const userData = authService.handleGoogleCallback();
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);

      // 🔌 Initialize sockets for Google OAuth callback
setTimeout(() => {
        socketManager.initializeAll();
      }, 100);
    }
    return userData;
  };

  // Logout function
  const logout = () => {
    // 🔌 Disconnect all sockets before clearing user data
socketManager.disconnectAll();

    localStorage.removeItem("user");
    clearPhotoCache(); // Clear profile photo cache on logout
    setUser(null);
    setIsAuthenticated(false);
  };

  // Update user data
  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        AUTH_ENDPOINTS.UPDATE_PROFILE,
        profileData
      );

      if (response.data) {
        const updatedUser = { ...user, ...response.data };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        toast.success("Profile updated successfully!");
        return response.data;
      }
    } catch (error) {
const errorMessage =
        error.response?.data?.message || "Failed to update profile";
      toast.error(errorMessage);
      throw handleApiError(error);
    }
  };

  // Update profile picture - FIXED to use correct 3-step process
  const updateProfilePicture = async (file) => {
    try {
      // Validate file type and size (frontend validation)
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!allowedTypes.includes(file.type)) {
        throw new Error("Only JPG, PNG, GIF, and WebP files are allowed");
      }

      if (file.size > maxSize) {
        throw new Error("File size must be less than 2MB");
      }

      // Step 1: Get pre-signed URL
      const uploadData = await profilePhotoService.getUploadUrl({
        filename: file.name,
        contentType: file.type,
      });

      if (!uploadData.success) {
        throw new Error(uploadData.message || "Failed to get upload URL");
      }

      // Step 2: Upload file directly to S3
      await profilePhotoService.uploadToS3(uploadData.uploadUrl, file);

      // Step 3: Update profile photo URL in database
      const updateData = await profilePhotoService.updateProfilePhoto(
        uploadData.key
      );

      if (!updateData.success) {
        throw new Error(updateData.message || "Failed to update profile photo");
      }

      // Update user context with new profile photo URL
      // IMPORTANT: profilePhotoUrl must come AFTER ...updateData.user spread
      // so the full S3 URL from the response isn't overwritten by a relative key
      const updatedUser = {
        ...user,
        ...updateData.user,
        profilePhotoUrl: updateData.profilePhotoUrl,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);

      // Update profile photo cache with new timestamp
      updatePhotoCache(
        updateData.profilePhotoUrl,
        updateData.user?.photoUpdatedAt || new Date().toISOString()
      );

      toast.success("Profile picture updated successfully!");

      return {
        success: true,
        profilePhotoUrl: updateData.profilePhotoUrl,
        user: updatedUser,
      };
    } catch (error) {
const errorMessage = error.message || "Failed to update profile picture";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Remove profile picture - FIXED to use correct service
  const removeProfilePicture = async () => {
    try {
      const result = await profilePhotoService.removeProfilePhoto();

      if (!result.success) {
        throw new Error(result.message || "Failed to remove profile photo");
      }

      // Update user context to remove profile photo
      // IMPORTANT: profilePhotoUrl: null must come AFTER ...result.user spread
      // so it isn't overwritten by a stale URL from the user object
      const updatedUser = {
        ...user,
        ...result.user,
        profilePhotoUrl: null,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);

      // Clear profile photo cache
      clearPhotoCache();

      toast.success("Profile picture removed successfully!");

      return result;
    } catch (error) {
const errorMessage = error.message || "Failed to remove profile picture";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${AUTH_ENDPOINTS.UPDATE_PROFILE}/password`,
        {
          currentPassword,
          newPassword,
        }
      );

      if (response.data) {
        toast.success("Password changed successfully!");
        return response.data;
      }
    } catch (error) {
const errorMessage =
        error.response?.data?.message || "Failed to change password";
      toast.error(errorMessage);
      throw handleApiError(error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        signup,
        googleLogin,
        handleGoogleCallback,
        saveOnboardingData,
        logout,
        updateUser,
        updateProfile,
        updateProfilePicture,
        removeProfilePicture,
        changePassword,
        fetchUserProfile,
        setUser,
        setIsAuthenticated,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
